
import { computed, useRoute } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore';
import useUtilities from '~/composable/useUtilities';
// Voucher feature composable - gracefully handles if stores are not available
import { useVoucherPriceAdjustment } from '~/composable/voucher/useVoucherPriceAdjustment';
export default {
  props: {
    hasSpecialPrices: {
      type: Boolean,
      default: false
    },
    productName: {
      type: String,
      default: ''
    },
    removingDiscountCode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['remove-discount-code'],
  setup() {
    const chosenVariantDetailsStore = useChosenVariantDetailsStore()
    const {
      chosenInstallment,
      chosenChannel,
      productDetails
    } = storeToRefs(chosenVariantDetailsStore)
    const route = useRoute()
    const isAccessoryCategory = computed(() => route.value?.params?.categoryslug?.toLowerCase() === 'tilbehoer')
    const isAccessoryProduct = computed(() => {
      const productType = productDetails.value?.chosenVariant?.productTypeKey
      return productType === 'accessory' || isAccessoryCategory.value
    })
    const { formatPrice } = useUtilities()

    // Voucher feature - use composable for price adjustments
    const {
      isVoucherFeatureAvailable,
      discountCodeAmount,
      showDiscountCode,
      calculateAdjustedPrice,
      getPriceFromCartIfVoucherApplies,
      formatDisplayPrice
    } = useVoucherPriceAdjustment(productDetails)
    const discount = computed(() => chosenChannel.value?.productBundlePrice?.discount)
    const basePrice = computed(() => chosenChannel.value?.productBundlePrice?.basePrice)
    const bindingPeriodPayment = computed(() => chosenChannel.value?.productBundlePrice?.bindingPeriodPayment)
    const minimumPrice = computed(() => chosenChannel.value?.productBundlePrice?.minimumPrice)
    const productWithSubscriptionPrice = computed(() => chosenChannel.value?.productBundlePrice?.productWithSubscriptionPrice)
    const cashPrice = computed(() => chosenChannel.value?.productBundlePrice?.cashPrice)

    // Adjusted prices after discount code is applied (voucher feature only)
    // Fallback to original prices if voucher feature is not available
    const adjustedCashPrice = computed(() => {
      const price = cashPrice.value || 0
      return calculateAdjustedPrice(price)
    })

    const adjustedProductWithSubscriptionPrice = computed(() => {
      const basePriceValue = productWithSubscriptionPrice.value || 0
      const expectedDiscountedPrice = calculateAdjustedPrice(basePriceValue)
      const cartPrice = getPriceFromCartIfVoucherApplies()
      if (cartPrice === null) {
        return expectedDiscountedPrice
      }

      // For some products, cart lineItemPrice can represent a different price context
      // than "Pris på mobil med rabat" on PDP. Use it only when it is near the
      // expected PDP discounted device price; otherwise fallback to expected value.
      const isCartPriceReasonable = Math.abs(cartPrice - expectedDiscountedPrice) <= 1
      if (isCartPriceReasonable) {
        return Math.max(0, cartPrice)
      }
      return expectedDiscountedPrice
    })

    const adjustedMinimumPrice = computed(() => {
      const price = minimumPrice.value || 0
      if (!isVoucherFeatureAvailable) {
        return price
      }
      return Math.max(0, price - discountCodeAmount.value)
    })

    const calculateInstallmentAmountFromPrice = (lineItemPrice, months) => {
      if (!lineItemPrice || !months || months === 0) {
        return 0
      }
      return Math.ceil((lineItemPrice / months) * 100) / 100
    }

    const calculateMonthlyTotal = (subscriptionPrice, installmentMonths) => {
      if (installmentMonths <= 0) {
        return subscriptionPrice
      }
      const installmentAmount = calculateInstallmentAmountFromPrice(adjustedProductWithSubscriptionPrice.value, installmentMonths)
      return subscriptionPrice + installmentAmount
    }

    const getMonthlyTotalOrFallback = (calculatedMonthlyTotal) => {
      if (calculatedMonthlyTotal > 0) {
        return calculatedMonthlyTotal
      }
      return chosenInstallment.value?.monthlyAmountWithSubscription || 0
    }

    const resolveMonthlyTotalPrice = () => {
      const installmentMonths = chosenInstallment.value?.months || 0
      const subscriptionPrice = chosenChannel.value?.productBundlePrice?.bundlePrice || 0
      const calculatedMonthlyTotal = calculateMonthlyTotal(subscriptionPrice, installmentMonths)
      return getMonthlyTotalOrFallback(calculatedMonthlyTotal)
    }

    const monthlyTotalPrice = computed(() => resolveMonthlyTotalPrice())

    // For accessory products, only show price lines when voucher code is applied (voucher feature only)
    const shouldShowPriceLines = computed(() => {
      if (!isAccessoryProduct.value) {
        return true
      }
      if (!isVoucherFeatureAvailable) {
        return false // Hide price lines for accessories without voucher feature
      }
      return showDiscountCode.value
    })

    return {
      formatPrice,
      formatDisplayPrice,
      chosenChannel,
      discount,
      basePrice,
      bindingPeriodPayment,
      minimumPrice,
      productWithSubscriptionPrice,
      cashPrice,
      monthlyTotalPrice,
      chosenInstallmentPeriod: computed(() => chosenInstallment.value?.months),
      isCash: computed(() => chosenChannel.value?.channelKey === 'Cash'),
      bundlePrice: computed(() => chosenChannel.value?.productBundlePrice?.bundlePrice),
      discountCodeDiscount: discountCodeAmount,
      hasAppliedDiscountCode: showDiscountCode,
      adjustedCashPrice,
      adjustedProductWithSubscriptionPrice,
      adjustedMinimumPrice,
      isAccessoryProduct,
      shouldShowPriceLines
    }
  }
}
